import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useT } from "@/i18n";
import { LumiMascot, SparkMascot } from "@/components/Mascots";
import HelloMascot from "@/components/HelloMascot";
import ModulesGrid from "@/components/ModulesGrid";
import { Stars, Clouds } from "@/components/SkyDecor";

const RainbowWord = ({ text }) => {
  const colors = ["#ef4444", "#f97316", "#facc15", "#22c55e", "#06b6d4", "#3b82f6", "#a855f7", "#ec4899"];
  return (
    <span>
      {text.split("").map((ch, i) => (
        <span key={i} className="bubble-text" style={{ color: colors[i % colors.length] }}>
          {ch}
        </span>
      ))}
    </span>
  );
};

export default function Landing() {
  const { t } = useT();
  return (
    <div className="min-h-screen sky-bg">
      <section className="relative overflow-hidden">
        <Stars />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-16 text-center">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block bg-white/90 rounded-full px-6 py-2 text-sm sm:text-base font-bold text-blue-900 shadow-lg"
            data-testid="hero-kicker"
          >
            {t.hero.kicker}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1, type: "spring", stiffness: 120 }}
            className="mt-6 font-fredoka font-bold text-4xl sm:text-6xl lg:text-7xl leading-[1.1]"
            data-testid="hero-title"
          >
            <span className="bubble-text text-white">{t.hero.title} </span>
            <span className="bubble-text text-yellow-300">{t.hero.titleIdeas}</span>
            <br />
            <span className="bubble-text text-white">{t.hero.titleIn} </span>
            <RainbowWord text={t.hero.titleAccent} />
          </motion.h1>

          <div className="mt-6 grid grid-cols-[1fr_auto_1fr] md:grid-cols-[1fr_1.4fr_1fr] items-center gap-2">
            <motion.div initial={{ opacity: 0, x: -40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }} className="flex justify-center">
              <HelloMascot mascot="lumi" delay={0.6} />
            </motion.div>
            <div className="hidden md:block">
              <p className="font-fredoka text-xl lg:text-2xl font-semibold text-blue-900">{t.hero.sub1}</p>
              <p className="mt-6 text-blue-900 font-bold">{t.hero.sub2}</p>
            </div>
            <span className="md:hidden" />
            <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="flex justify-center">
              <HelloMascot mascot="spark" delay={0.9} />
            </motion.div>
          </div>
          <div className="md:hidden mt-4 text-blue-900">
            <p className="font-fredoka text-lg font-semibold">{t.hero.sub1}</p>
            <p className="mt-3 text-sm font-bold">{t.hero.sub2}</p>
          </div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/sign-up" className="glossy-btn inline-block bg-yellow-300 text-blue-900 font-fredoka font-bold text-2xl sm:text-3xl px-12 py-4 rounded-full" data-testid="hero-start-btn">
              {t.hero.ctaStart}
            </Link>
            <Link to="/parents" className="glossy-btn inline-block bg-white text-blue-900 font-bold text-lg px-8 py-3 rounded-full" data-testid="hero-parents-btn">
              {t.hero.ctaParents}
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="relative overflow-hidden learn-bg pt-14 pb-44 sm:pb-60" data-testid="learn-section">
        <Stars />
        <div className="relative max-w-6xl mx-auto px-3 sm:px-6">
          <div className="text-center">
            <h2 className="font-fredoka text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight" data-testid="learn-title">
              <span className="bubble-text text-white">{t.learnA} </span>
              <span className="bubble-text text-yellow-300">{t.learnB}</span>
              <span className="bubble-text text-white">{t.learnC === "?" ? "?" : ` ${t.learnC}`}</span>
            </h2>
            <p className="inline-block mt-4 bg-white/90 rounded-full px-5 py-2 text-sm sm:text-lg font-bold text-blue-900 shadow-lg">
              {t.learnSub}
            </p>
          </div>
          <div className="mt-10 relative z-10">
            <ModulesGrid />
          </div>
        </div>
        <Clouds />
        <div className="absolute bottom-2 left-2 sm:left-8 z-20">
          <LumiMascot className="w-28 sm:w-44 lg:w-52" />
        </div>
        <div className="absolute bottom-2 right-2 sm:right-8 z-20">
          <SparkMascot className="w-28 sm:w-44 lg:w-52" />
        </div>
      </section>
    </div>
  );
}
